/**
 * Avviso nell'editor del tipo di assenza: **questo tipo si auto-approva**, quindi il
 * workflow configurato non verrà applicato.
 *
 * Perché serve. `approvalWorkflow` decide quanti umani servono *dopo* il submit, ma il
 * submit ha già due strade di auto-approvazione che lo precedono, ed è la §2 della
 * chiusura BE: sul catalogo CCNL sono attive su **8 tipi su 12**, dove impostare un
 * workflow è completamente inerte — nessun errore, nessun avviso, semplicemente non ha
 * effetto. Questo è l'unico momento utile per dirlo: mentre si configura.
 *
 * La seconda strada dipende dal tenant-attribute `leave.nonRejectableAutoApprove`
 * (`true` su tutti i tenant dev), non dal tipo: va letta qui, non nella funzione pura.
 */
import { Alert, Text } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useTenantConfig } from '@/shared/hooks/useTenantConfig';
import { autoApproveWarning, type AutoApproveShape } from './approvalWorkflow';

export function LeaveAutoApproveNotice({ values }: { values: Record<string, unknown> }) {
  const cfg = useTenantConfig();
  // Default `true`: è il valore su tutti i tenant dev e, finché la config non è
  // caricata, tacere sarebbe la scelta peggiore — l'avviso serve proprio a chi sta
  // configurando in quel momento.
  const nonRejectableAutoApprove = cfg.getBool('leave.nonRejectableAutoApprove', true);

  const shape: AutoApproveShape = {
    approvalWorkflow: (values.approvalWorkflow as string | null) ?? null,
    autoApprovePolicy: (values.autoApprovePolicy as string | null) ?? null,
    isRejectable: values.isRejectable === true,
    requiresDocument: (values.requiresDocument as string | null) ?? null,
  };
  const w = autoApproveWarning(shape, nonRejectableAutoApprove);
  if (!w) return null;

  // Giallo solo quando l'auto-approvazione è certa e c'è un workflow esplicito che
  // verrebbe ignorato: è l'unico caso in cui la configurazione mente. Altrimenti è
  // un'informazione, non un problema.
  const contradicts = w.always && Boolean(shape.approvalWorkflow);
  return (
    <Alert
      variant="light"
      color={contradicts ? 'yellow' : 'gray'}
      icon={contradicts ? <IconAlertTriangle size={16} /> : <IconInfoCircle size={16} />}
      styles={{ message: { fontSize: 'var(--fs-compact)' } }}
    >
      <Text fz="xs" c="var(--ink-700)">{w.message}</Text>
      {contradicts && (
        <Text fz="xs" c="var(--ink-500)" mt={6}>
          Il workflow impostato qui sopra non avrà effetto su questo tipo finché resta così configurato.
        </Text>
      )}
    </Alert>
  );
}
